import React, { useState } from 'react';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaFacebook, FaInstagram, FaLinkedin, FaYoutube, FaSpinner, FaWhatsapp } from 'react-icons/fa';
import { RiTwitterXFill } from 'react-icons/ri';

const Contatti = () => {
  // State for form fields
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    messageType: 'admin' // Default to admin
  });
  
  // State for form submission and validation
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  // Validate form fields
  const validateForm = () => {
    const tempErrors = {};
    let isValid = true;
    
    if (!formData.name.trim()) {
      tempErrors.name = "Il nome è obbligatorio";
      isValid = false;
    }
    
    if (!formData.messageType) {
      tempErrors.messageType = "Seleziona il tipo di messaggio";
      isValid = false;
    }
    
    if (!formData.email.trim()) {
      tempErrors.email = "L'email è obbligatoria";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      tempErrors.email = "Inserisci un indirizzo email valido";
      isValid = false;
    }
    
    if (!formData.message.trim()) {
      tempErrors.message = "Il messaggio è obbligatorio";
      isValid = false;
    }
    
    setErrors(tempErrors);
    return isValid;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset any previous API errors
    setApiError(null);
    
    if (validateForm()) {
      try {
        setIsLoading(true);
        
        // Send the data to our backend API
        const response = await fetch('http://localhost:3001/api/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || 'Si è verificato un errore durante l\'invio dell\'email');
        }
        
        console.log("Form data submitted successfully:", data);
        setIsSubmitted(true);
        
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          messageType: 'admin'
        });
      } catch (error) {
        console.error("Error submitting form:", error);
        setApiError(error.message || 'Si è verificato un errore durante l\'invio. Riprova più tardi.');
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  return (
    <div className="bg-gray-50">
      {/* Header Section */}
      <div className="bg-accent text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Contatti</h1>
          <p className="text-xl max-w-3xl">
            Siamo qui per aiutarti. Non esitare a contattarci per qualsiasi domanda, informazione o supporto di cui hai bisogno.
          </p>
        </div>
      </div>
      
      {/* Contact Information Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="w-full mb-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:shadow-2xl transition-all duration-300">
            <div className="relative">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-gradient-to-r from-accent/5 to-primary/5">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(0,0,0,0.05) 1px, transparent 0)',
                  backgroundSize: '20px 20px'
                }}></div>
              </div>
              
              {/* Content */}
              <div className="relative p-8">
                {/* Header */}
                <div className="text-center mb-8">
                  <div className="inline-block p-4 bg-accent/10 rounded-full mb-4">
                    <FaEnvelope className="text-accent text-4xl transform transition-transform duration-300 hover:scale-110" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">I Nostri Contatti Email</h2>
                  <p className="text-gray-600">Scegli l'indirizzo più appropriato per la tua richiesta</p>
                </div>

                {/* Email Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Amministrazione */}
                  <div className="p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h3 className="ml-3 font-semibold text-gray-800">Amministrazione</h3>
                    </div>
                    <a href="mailto:amministrazione.robinhoodroma@gmail.com" 
                       className="text-accent hover:text-accent/70 transition-colors duration-300 break-all">
                      amministrazione.robinhoodroma@gmail.com
                    </a>
                  </div>

                  {/* Volontariato */}
                  <div className="p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                      </div>
                      <h3 className="ml-3 font-semibold text-gray-800">Volontariato</h3>
                    </div>
                    <a href="mailto:volontariatorobinhoodromaodv@gmail.com" 
                       className="text-accent hover:text-accent/70 transition-colors duration-300 break-all">
                      volontariatorobinhoodromaodv@gmail.com
                    </a>
                  </div>

                  {/* Presidente */}
                  <div className="p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <h3 className="ml-3 font-semibold text-gray-800">Presidente</h3>
                    </div>
                    <a href="mailto:presidente.robinhoodromaodv@gmail.com" 
                       className="text-accent hover:text-accent/70 transition-colors duration-300 break-all">
                      presidente.robinhoodromaodv@gmail.com
                    </a>
                  </div>

                  {/* Quesiti Clinici */}
                  <div className="p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="ml-3 font-semibold text-gray-800">Quesiti Clinici</h3>
                    </div>
                    <a href="mailto:quesitirare.robinhoodromaodv@gmail.com" 
                       className="text-accent hover:text-accent/70 transition-colors duration-300 break-all">
                      quesitirare.robinhoodromaodv@gmail.com
                    </a>
                  </div>

                  {/* PEC */}
                  <div className="p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h3 className="ml-3 font-semibold text-gray-800">PEC</h3>
                    </div>
                    <a href="mailto:robinhoodromaodv@pec.it" 
                       className="text-accent hover:text-accent/70 transition-colors duration-300 break-all">
                      robinhoodromaodv@pec.it
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {/* Card Indirizzo */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:shadow-2xl transition-all duration-300 h-full">
            <div className="relative h-full">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-gradient-to-r from-accent/5 to-primary/5">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(0,0,0,0.05) 1px, transparent 0)',
                  backgroundSize: '20px 20px'
                }}></div>
              </div>
              
              {/* Content */}
              <div className="relative p-8 h-full flex items-center justify-center">
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 bg-blue-100 rounded-full mb-4">
                    <FaMapMarkerAlt className="text-blue-600 text-3xl transform transition-transform duration-300 hover:scale-110" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">La Nostra Sede</h3>
                  <div className="text-gray-600">
                    <p className="mb-2"><strong>EMATOLOGIA</strong></p>
                    <p className="mb-2">Via Benevento 6, 00161 Roma</p>
                    <p>1° piano ambulatori e accettazione<br />malattie ematologiche rare pediatriche</p>
                  </div>
                  <div></div>
                </div>
              </div>
            </div>
          </div>

          {/* Card Telefono */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:shadow-2xl transition-all duration-300">
            <div className="relative">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-gradient-to-r from-accent/5 to-primary/5">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(0,0,0,0.05) 1px, transparent 0)',
                  backgroundSize: '20px 20px'
                }}></div>
              </div>
              
              {/* Content */}
              <div className="relative p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 bg-green-100 rounded-full mb-4">
                    <FaPhone className="text-green-600 text-3xl transform transition-transform duration-300 hover:scale-110" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">Telefono</h3>
                  <div className="text-gray-600 space-y-3">
                    <div>
                      <p className="mb-1"><strong>Telefono</strong></p>
                      <a 
                        href="tel:+393295585666"
                        className="text-accent hover:text-accent/70 transition-colors duration-300"
                      >
                        +39 329 558 5666
                      </a>
                    </div>
                    <div>
                      <p className="mb-1"><strong>Orario</strong></p>
                      <p>Lun - Ven, 9:00 - 18:00</p>
                    </div>
                    <div className="flex items-center justify-center mt-4 pt-4 border-t border-gray-200">
                      <a 
                        href="https://wa.me/393295585666" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex items-center hover:bg-green-50 p-3 rounded-lg transition-all duration-300 w-full"
                      >
                        <div className="p-3 bg-green-100 rounded-full mr-3">
                          <FaWhatsapp className="text-green-600 text-2xl" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold mb-1">WhatsApp</p>
                          <p className="text-green-600 hover:text-green-700 transition-colors duration-300">
                            +39 329 558 5666
                          </p>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contact Form Section */}
      <div className="bg-gray-100 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="md:flex">
              <div className="md:w-1/2 bg-accent text-white p-8">
                <h2 className="text-3xl font-bold mb-4">Parlaci di te</h2>
                <p className="mb-6">
                  Compila il modulo selezionando il tipo di messaggio appropriato e ti risponderemo al più presto. Il tuo messaggio è importante per noi.
                </p>
                <div className="mb-6">
                  <h4 className="text-xl font-semibold mb-2">Orari di apertura:</h4>
                  <p>Lunedì - Venerdì: 9:00 - 18:00</p>
                  <p>Sabato: 9:00 - 13:00</p>
                  <p>Domenica: Chiuso</p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2">Seguici:</h4>
                  <div className="flex space-x-4">
                    <a href="https://www.facebook.com/share/1Fr5hwYqMX/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="text-white hover:text-white/70 transition-colors duration-300">
                      <FaFacebook size={24} />
                    </a>
                    <a href="https://x.com/RobinHoodODV" target="_blank" rel="noopener noreferrer" className="text-white hover:text-white/70 transition-colors duration-300">
                      <RiTwitterXFill size={24} />
                    </a>
                    <a href="https://www.instagram.com/robinhoodromaodv?igsh=MXR0c2lhNWVzYXRzag==" target="_blank" rel="noopener noreferrer" className="text-white hover:text-white/70 transition-colors duration-300">
                      <FaInstagram size={24} />
                    </a>
                    <a href="https://www.linkedin.com/company/associazione-robin-hood-roma-odv/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-white/70 transition-colors duration-300">
                      <FaLinkedin size={24} />
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="md:w-1/2 p-8">
                {isSubmitted ? (
                  <div className="bg-green-100 text-green-700 p-4 rounded-lg mb-4">
                    <h3 className="font-bold text-xl mb-2">Grazie per averci contattato!</h3>
                    <p>Abbiamo ricevuto il tuo messaggio e ti risponderemo al più presto.</p>
                  </div>
                ) : (
                  <>
                    {apiError && (
                      <div className="bg-accent/10 text-accent p-4 rounded-lg mb-4">
                        <h3 className="font-bold text-xl mb-2">Errore</h3>
                        <p>{apiError}</p>
                      </div>
                    )}
                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Nome e Cognome*</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent ${errors.name ? 'border-accent' : 'border-gray-300'}`}
                      />
                      {errors.name && <p className="text-accent text-sm mt-1">{errors.name}</p>}
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="email" className="block text-gray-700 font-medium mb-2">Email*</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent ${errors.email ? 'border-accent' : 'border-gray-300'}`}
                      />
                      {errors.email && <p className="text-accent text-sm mt-1">{errors.email}</p>}
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">Telefono</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="messageType" className="block text-gray-700 font-medium mb-2">Tipo di messaggio*</label>
                      <select
                        id="messageType"
                        name="messageType"
                        value={formData.messageType}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent ${errors.messageType ? 'border-accent' : 'border-gray-300'}`}
                      >
                        <option value="admin">Amministrazione (ricevute, contabilità, banche)</option>
                        <option value="volunteer">Volontariato</option>
                        <option value="president">Comunicazione per la Presidente</option>
                        <option value="clinical">Quesiti Clinici</option>
                      </select>
                      {errors.messageType && <p className="text-accent text-sm mt-1">{errors.messageType}</p>}
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="subject" className="block text-gray-700 font-medium mb-2">Oggetto</label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                      />
                    </div>
                    
                    <div className="mb-6">
                      <label htmlFor="message" className="block text-gray-700 font-medium mb-2">Messaggio*</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="5"
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent ${errors.message ? 'border-accent' : 'border-gray-300'}`}
                      ></textarea>
                      {errors.message && <p className="text-accent text-sm mt-1">{errors.message}</p>}
                    </div>
                    
                    <button 
                      type="submit" 
                      disabled={isLoading}
                      className={`bg-accent text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent/90 transition duration-300 flex items-center ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isLoading ? (
                        <>
                          <FaSpinner className="animate-spin mr-2" />
                          Invio in corso...
                        </>
                      ) : (
                        'Invia Messaggio'
                      )}
                    </button>
                  </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Office Locations Section */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-12">La Nostra Sede</h2>
        
        <div className="grid md:grid-cols-1">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gray-300 flex items-center justify-center" style={{ height: '400px' }}>
              {/* Google Maps integration */}
              <div className="mapouter" style={{ position: 'relative', textAlign: 'right', width: '100%', height: '400px' }}>
                <div className="gmap_canvas" style={{ overflow: 'hidden', background: 'none!important', width: '100%', height: '400px' }}>
                  <iframe 
                    className="gmap_iframe" 
                    style={{ height: '400px' }} 
                    width="100%" 
                    frameBorder="0" 
                    scrolling="no" 
                    marginHeight="0" 
                    marginWidth="0" 
                    src="https://maps.google.com/maps?width=600&amp;height=400&amp;hl=en&amp;q=Via Benevento 6 00161 ROMA&amp;t=k&amp;z=17&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
                  ></iframe>
                </div>
              </div>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-bold mb-2">Dove siamo</h3>
              <p className="text-gray-600 mb-4">
                Via Benevento 6, 00161 ROMA<br />
                Ematologia, Dipartimento di Medicina Traslazionale e di Precisione,<br />
                Sapienza Università di Roma, AOU Policlinico Umberto I<br />
                Tel: +39 3295585666<br />
                Email: robinhoodromaodv@gmail.com
              </p>
              <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/90 font-medium">
                Indicazioni stradali →
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Social Media Section */}
      <div className="bg-accent text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8">Seguici sui Social Media</h2>
          <div className="flex justify-center space-x-8 mb-10">
            <a href="https://www.facebook.com/share/1Fr5hwYqMX/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="bg-white text-accent hover:bg-accent hover:text-white p-4 rounded-full transition-all duration-300">
              <FaFacebook size={32} />
            </a>
            <a href="https://x.com/RobinHoodODV" target='_blank' rel="noopener noreferrer" className="bg-white text-accent hover:bg-accent hover:text-white p-4 rounded-full transition-all duration-300">
              <RiTwitterXFill size={32} />
            </a>
            <a href="https://www.instagram.com/robinhoodromaodv?igsh=MXR0c2lhNWVzYXRzag==" target='_blank' rel="noopener noreferrer" className="bg-white text-accent hover:bg-accent hover:text-white p-4 rounded-full transition-all duration-300">
              <FaInstagram size={32} />
            </a>
            <a href="https://www.linkedin.com/company/associazione-robin-hood-roma-odv/" target='_blank' rel="noopener noreferrer" className="bg-white text-accent hover:bg-accent hover:text-white p-4 rounded-full transition-all duration-300">
              <FaLinkedin size={32} />
            </a>
            <a href="https://www.youtube.com/@RobinHoodRomaOdv" target='_blank' className="bg-white text-accent hover:bg-accent hover:text-white p-4 rounded-full transition-all duration-300">
              <FaYoutube size={32} />
            </a>
          </div>
          <p className="text-xl">
            Resta aggiornato sulle nostre attività, eventi e storie di successo. Unisciti alla nostra community!
          </p>
          <div className="mt-8">
            <h3 className="text-2xl font-bold mb-4">Hashtag ufficiali</h3>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-accent/90 px-4 py-2 rounded-full text-lg">#RobinHoodRoma</span>
              <span className="bg-accent/90 px-4 py-2 rounded-full text-lg">#MalattieRare</span>
              <span className="bg-accent/90 px-4 py-2 rounded-full text-lg">#InsieméPiùForti</span>
              <span className="bg-accent/90 px-4 py-2 rounded-full text-lg">#SosteniamociInsieme</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contatti;
